'use client';

import { useState, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface TimezoneSelectorProps {
  value: string;
  onChange: (timezone: string) => void;
  compact?: boolean;
}

// Common timezones grouped by region
const timezonesByRegion = {
  'North America': [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'America/Honolulu',
    'America/Toronto',
    'America/Vancouver',
    'America/Mexico_City',
  ],
  'South America': [
    'America/Sao_Paulo',
    'America/Buenos_Aires',
    'America/Santiago',
    'America/Lima',
    'America/Bogota',
  ],
  Europe: [
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Europe/Madrid',
    'Europe/Rome',
    'Europe/Amsterdam',
    'Europe/Zurich',
    'Europe/Stockholm',
    'Europe/Oslo',
    'Europe/Copenhagen',
    'Europe/Vienna',
    'Europe/Brussels',
    'Europe/Dublin',
    'Europe/Lisbon',
    'Europe/Warsaw',
    'Europe/Prague',
    'Europe/Budapest',
    'Europe/Athens',
    'Europe/Istanbul',
    'Europe/Moscow',
  ],
  'Asia & Middle East': [
    'Asia/Dubai',
    'Asia/Riyadh',
    'Asia/Jerusalem',
    'Asia/Baghdad',
    'Asia/Tehran',
    'Asia/Karachi',
    'Asia/Mumbai',
    'Asia/Kolkata',
    'Asia/Dhaka',
    'Asia/Bangkok',
    'Asia/Singapore',
    'Asia/Kuala_Lumpur',
    'Asia/Jakarta',
    'Asia/Hong_Kong',
    'Asia/Shanghai',
    'Asia/Beijing',
    'Asia/Seoul',
    'Asia/Tokyo',
    'Asia/Manila',
    'Asia/Taipei',
    'Asia/Hanoi',
  ],
  'Australia & Pacific': [
    'Australia/Perth',
    'Australia/Adelaide',
    'Australia/Darwin',
    'Australia/Brisbane',
    'Australia/Sydney',
    'Australia/Melbourne',
    'Australia/Canberra',
    'Australia/Hobart',
    'Pacific/Auckland',
    'Pacific/Fiji',
    'Pacific/Honolulu',
  ],
  Africa: [
    'Africa/Cairo',
    'Africa/Johannesburg',
    'Africa/Lagos',
    'Africa/Nairobi',
    'Africa/Casablanca',
    'Africa/Accra',
  ],
};

// Flatten all timezones for search
const allTimezones = Object.values(timezonesByRegion).flat();

export function TimezoneSelector({ value, onChange, compact }: TimezoneSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');

  // Get current UTC offset for a timezone
  const getUTCOffset = (timezone: string): string => {
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'shortOffset',
      });
      const parts = formatter.formatToParts(now);
      const offsetPart = parts.find((p) => p.type === 'timeZoneName');
      return offsetPart?.value || '';
    } catch {
      return '';
    }
  };

  // Format timezone for display
  const formatTimezone = (timezone: string): string => {
    const offset = getUTCOffset(timezone);
    const city = timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
    return `${city} (${offset})`;
  };

  // Filter timezones based on search
  const filteredTimezones = useMemo(() => {
    if (!searchTerm) return null; // Show grouped view when no search

    const term = searchTerm.toLowerCase();
    return allTimezones.filter(
      (tz) => tz.toLowerCase().includes(term) || formatTimezone(tz).toLowerCase().includes(term),
    );
  }, [searchTerm]);

  return (
    <div className={compact ? '' : 'space-y-2'}>
      {!compact && <Label>Timezone</Label>}
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          size={compact ? 'sm' : 'default'}
          className={
            compact
              ? 'text-xs w-[160px] overflow-hidden min-w-0 [&_[data-slot=select-value]]:truncate'
              : 'w-full'
          }
        >
          <SelectValue placeholder="Select timezone">
            {value ? formatTimezone(value) : 'Select timezone'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-80">
          {/* Search input */}
          <div className="p-2 sticky top-0 bg-background border-b">
            <Input
              placeholder="Search timezones..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>

          {/* Timezone list */}
          {filteredTimezones ? (
            // Search results
            filteredTimezones.length > 0 ? (
              filteredTimezones.map((timezone) => (
                <SelectItem key={timezone} value={timezone}>
                  {formatTimezone(timezone)}
                </SelectItem>
              ))
            ) : (
              <div className="p-2 text-sm text-muted-foreground">No timezones found</div>
            )
          ) : (
            // Grouped view
            Object.entries(timezonesByRegion).map(([region, timezones]) => (
              <div key={region}>
                <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground bg-muted">
                  {region}
                </div>
                {timezones.map((timezone) => (
                  <SelectItem key={timezone} value={timezone}>
                    {formatTimezone(timezone)}
                  </SelectItem>
                ))}
              </div>
            ))
          )}
        </SelectContent>
      </Select>
    </div>
  );
}
